import { Injectable } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { map, Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import {
  BomConsumptionResponse,
  FactoryBatchStock,
  FactoryStockItem,
  ProductStockAdjustment,
  StockAdjustmentFilters,
  StockAdjustmentRequest,
  StockAdjustmentScope,
  InventoryStockItem,
  ProductionEntry,
  ProductionEntryInsight,
  ProductionEntryReqBody,
  ProductionStockItem,
  ProductTransfer,
  ProductTransferRequest,
  RawMaterialConsumption,
  RawMaterialConsumptionRequest,
  WarehouseBatchStock,
} from '../production-inventory.model';
import { PaginatedResponse } from '../../common/models/common.model';
import { buildHttpParams } from '../../utils';
import { Product } from '../../products/product.model';

@Injectable({
  providedIn: 'root',
})
export class ProductionInventoryService {
  private baseUrl: string = 'production-entries/';
  private stockUrl: string = 'products/';
  private consumptionUrl: string = 'raw-material-consumptions/';
  private factoryStockUrl = 'factory-stocks/';
  private factoryBatchStockUrl = 'factory-batch-stocks/';
  private productionStockUrl = 'production-stocks/';
  private warehouseBatchStockUrl = 'warehouse-batch-stocks/';
  private productTransfersUrl = 'product-transfers/';
  private inventoryStockUrl = 'inventory-stocks/';
  private factoryStockAdjustmentUrl = 'factory-stock-adjustments/';
  private warehouseStockAdjustmentUrl = 'warehouse-stock-adjustments/';

  constructor(private apiService: ApiService) {}

  public get(params: any): Observable<PaginatedResponse<ProductionEntry>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.baseUrl, queryParams);
  }

  public get_stock(params = {}): Observable<PaginatedResponse<Product>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.stockUrl, queryParams);
  }

  public post(reqBody: ProductionEntryReqBody): Observable<ProductionEntry[]> {
    return this.apiService.post(this.baseUrl, reqBody);
  }

  /** Admin-only. 204 on success; the backend reverses every stock movement the entry caused. */
  public deleteProductionEntry(id: string): Observable<void> {
    return this.apiService.delete(this.baseUrl + id + '/');
  }

  public years(): Observable<{ years: number[] }> {
    return this.apiService.get(this.baseUrl + 'years');
  }

  public insights(params: any = {}): Observable<ProductionEntryInsight[]> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.baseUrl + 'insights', queryParams);
  }

  public postConsumption(reqBody: RawMaterialConsumptionRequest): Observable<RawMaterialConsumption> {
    return this.apiService.post(this.consumptionUrl, reqBody);
  }

  public getConsumptions(params: any = {}): Observable<RawMaterialConsumption[]> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.consumptionUrl, queryParams);
  }

  public deleteConsumption(id: string): Observable<void> {
    return this.apiService.delete(this.consumptionUrl + id + '/');
  }

  public export_stock(params: any = {}): Observable<{ report_url: string; message: string }> {
    return this.apiService.post(this.stockUrl + 'export/', params);
  }

  public export_production(q: any = {}): Observable<Blob> {
    const params = buildHttpParams(q);
    return this.apiService.getBlob(this.baseUrl + 'export-production/', params);
  }

  public export_factory_batch_stock(q: any = {}): Observable<Blob> {
    const params = buildHttpParams(q);
    return this.apiService.getBlob('factory-batch-stocks/export-factory-stock/', params);
  }

  public export_product_transfers(q: any = {}): Observable<Blob> {
    const params = buildHttpParams(q);
    return this.apiService.getBlob('product-transfers/export-transfers/', params);
  }

  public exportWarehouseStock(q: any = {}): Observable<HttpResponse<Blob>> {
    const params = buildHttpParams(q);
    return this.apiService.getBlobResponse('warehouse-batch-stocks/export-warehouse-stock/', params);
  }

  public getFactoryStocks(params: any = {}): Observable<PaginatedResponse<FactoryStockItem>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.factoryStockUrl, queryParams);
  }

  public getFactoryBatchStocks(params: any = {}): Observable<PaginatedResponse<FactoryBatchStock>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.factoryBatchStockUrl, queryParams);
  }

  public getWarehouseBatchStocks(params: any = {}): Observable<PaginatedResponse<WarehouseBatchStock>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.warehouseBatchStockUrl, queryParams);
  }

  public getProductionStocks(params: any = {}): Observable<PaginatedResponse<ProductionStockItem>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.productionStockUrl, queryParams);
  }

  public getProductTransfers(params: any = {}): Observable<PaginatedResponse<ProductTransfer>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.productTransfersUrl, queryParams);
  }

  public postProductTransfer(body: ProductTransferRequest): Observable<ProductTransfer> {
    return this.apiService.post(this.productTransfersUrl, body);
  }

  /** Admin-only. 204 on success; the units go back to the factory go-down. */
  public deleteProductTransfer(id: string): Observable<void> {
    return this.apiService.delete(this.productTransfersUrl + id + '/');
  }

  public getInventoryStocks(params: any = {}): Observable<PaginatedResponse<InventoryStockItem>> {
    const queryParams = buildHttpParams(params);
    return this.apiService.get(this.inventoryStockUrl, queryParams);
  }

  /**
   * Admin-only. `quantity` is signed and in units. On the factory endpoint the factory is
   * derived from the batch, so the body carries no container; on the warehouse endpoint
   * `warehouse` is required, because one batch can sit in several of them.
   */
  public createStockAdjustment(
    scope: StockAdjustmentScope,
    body: StockAdjustmentRequest
  ): Observable<ProductStockAdjustment> {
    return this.apiService
      .post<any>(this.adjustmentUrl(scope), body)
      .pipe(map(raw => normaliseStockAdjustment(raw, scope)));
  }

  /** Admin-only, paginated, newest first. Relation filters take UUIDs, never names. */
  public getStockAdjustments(
    scope: StockAdjustmentScope,
    filters: StockAdjustmentFilters = {}
  ): Observable<PaginatedResponse<ProductStockAdjustment>> {
    const queryParams = buildHttpParams(filters as Record<string, string>);

    return this.apiService.get<PaginatedResponse<any>>(this.adjustmentUrl(scope), queryParams).pipe(
      map(response => ({
        ...response,
        results: (response.results ?? []).map(raw => normaliseStockAdjustment(raw, scope)),
      }))
    );
  }

  private adjustmentUrl(scope: StockAdjustmentScope): string {
    return scope === 'factory' ? this.factoryStockAdjustmentUrl : this.warehouseStockAdjustmentUrl;
  }

  public getBomConsumption(entryId: string): Observable<BomConsumptionResponse> {
    return this.apiService.get(this.baseUrl + entryId + '/bom-consumption/');
  }
}

/**
 * The two endpoints differ only in whether the container is keyed `factory*` or
 * `warehouse*`. Folding that here keeps the branch out of every component downstream.
 */
function normaliseStockAdjustment(raw: any, scope: StockAdjustmentScope): ProductStockAdjustment {
  const isFactory = scope === 'factory';

  return {
    ...raw,
    container: isFactory ? raw.factory : raw.warehouse,
    container_name: isFactory ? raw.factory_name : raw.warehouse_name,
    container_units_available_after: isFactory
      ? raw.factory_units_available_after
      : raw.warehouse_units_available_after,
  };
}
